import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { 
  ThumbsUp, 
  ThumbsDown, 
  BarChart3, 
  MessageSquare,
  Tag,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Percent
} from 'lucide-react';

export default function RLHFDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState('ALL'); // 'ALL' | 'up' | 'down'
  const [tagFilter, setTagFilter] = useState('ALL');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/agent/admin/rlhf-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setStats(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Gagal memuat data telemetri RLHF.');
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!id) return;
    if (!window.confirm('Hapus log koreksi RLHF ini secara permanen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/agent/admin/rlhf/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStats(true);
    } catch (err) {
      console.error(err);
      alert('Gagal menghapus catatan feedback.');
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!stats || !stats.recent) return;
    const wb = XLSX.utils.book_new();

    const exportRows = stats.recent.map((r, idx) => ({
      No: idx + 1,
      Rating: r.rating === 'up' ? 'Upvote (Puas)' : 'Downvote (Koreksi)',
      Error_Tags: r.error_tag || '-',
      Pertanyaan_User: r.question || '-',
      Catatan_Ahli: r.comment || '-',
      Waktu_Koreksi: r.created_at ? new Date(r.created_at).toLocaleString('id-ID') : '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, ws, "RLHF_Feedback");
    XLSX.writeFile(wb, `Tacia_RLHF_Feedback_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // All unique error tags for filtering
  const uniqueTags = useMemo(() => {
    if (!stats || !stats.byTag) return [];
    return stats.byTag.map(t => t.tag).filter(Boolean);
  }, [stats]);

  // Filtered recent items
  const filteredItems = useMemo(() => {
    if (!stats || !stats.recent) return [];
    return stats.recent.filter(item => {
      const matchRating = ratingFilter === 'ALL' || item.rating === ratingFilter;
      const matchTag = tagFilter === 'ALL' || (item.error_tag && item.error_tag.includes(tagFilter));
      const matchSearch = !searchQuery || 
        (item.question && item.question.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.comment && item.comment.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.error_tag && item.error_tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchRating && matchTag && matchSearch;
    });
  }, [stats, ratingFilter, tagFilter, searchQuery]);

  const totalFiltered = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const page = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (page - 1) * itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] gap-3">
        <div className="animate-spin w-8 h-8 border-3 border-brand border-t-transparent rounded-full" />
        <span className="text-xs font-medium text-text-sec">Memuat telemetri RLHF...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-6">
        <div className="text-center p-8 max-w-md bg-card-bg border border-ledger-border rounded-2xl shadow-lg">
          <div className="w-12 h-12 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4 border border-rose-500/20">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-bold text-text-pri">Gagal Memuat Data</h2>
          <p className="text-text-sec text-xs mt-2">{error}</p>
          <button 
            onClick={() => fetchStats(false)} 
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-brand text-white rounded-xl text-xs font-medium hover:bg-brand/90 transition shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  if (!stats) return null;
  const { summary, byTag } = stats;

  return (
    <div className="min-h-full bg-paper p-4 sm:p-8 overflow-y-auto space-y-6">
      {/* ─── HEADER BAR ─── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-ledger-border pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-brand-low border border-brand/20 text-brand text-[10px] font-bold uppercase tracking-wider mb-2">
            <Sparkles className="w-3 h-3" />
            <span>TACIA Model Alignment Center</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
            Reinforcement Learning Analytics
          </h1>
          <p className="text-text-sec text-xs sm:text-sm mt-1">
            Evaluasi kepuasan jawaban, analisis pola kekeliruan, dan arsip koreksi pakar pajak terhadap model TACIA.
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-2.5">
          {/* Export XLS */}
          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri rounded-xl text-xs font-semibold transition shadow-2xs hover:bg-surface-hover"
            title="Export Excel (.xlsx)"
          >
            <Download className="w-3.5 h-3.5 text-brand" />
            <span>Export XLS</span>
          </button>

          {/* Refresh */}
          <button
            onClick={() => fetchStats(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri rounded-xl text-xs font-semibold transition shadow-2xs hover:bg-surface-hover disabled:opacity-60"
            title="Refresh Data"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-text-sec ${refreshing ? "animate-spin text-brand" : ""}`} />
            <span>{refreshing ? "Menyinkron..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {/* ─── 4 SUMMARY CARDS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Total Feedback */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between group hover:border-brand/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Total Feedback</span>
            <div className="w-8 h-8 rounded-lg bg-brand-low border border-brand/20 text-brand flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
              {Number(summary.total_feedback || 0).toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-text-sec">
              <span>Umpan balik pakar & user</span>
            </div>
          </div>
        </div>

        {/* 2. Total Upvotes */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between group hover:border-emerald-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Tervalidasi Sesuai</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center">
              <ThumbsUp className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">
              {Number(summary.total_upvotes || 0).toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-text-sec">
              <span>Jawaban terkonfirmasi tepat</span>
            </div>
          </div>
        </div>

        {/* 3. Total Downvotes */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between group hover:border-rose-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Koreksi / Evaluasi</span>
            <div className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center justify-center">
              <ThumbsDown className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-rose-600 dark:text-rose-400 tracking-tight">
              {Number(summary.total_downvotes || 0).toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-text-sec">
              <span>Masukan untuk perbaikan model</span>
            </div>
          </div>
        </div>

        {/* 4. Satisfaction Rate */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between group hover:border-brand/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Rasio Kepuasan</span>
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-500 flex items-center justify-center">
              <Percent className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-brand tracking-tight">
              {summary.upvote_ratio || 0}%
            </div>
            <div className="w-full bg-paper rounded-full h-1.5 overflow-hidden border border-ledger-border mt-2">
              <div
                className="h-full bg-brand rounded-full transition-all duration-500"
                style={{ width: `${Math.min(Math.max(Number(summary.upvote_ratio || 0), 0), 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ─── SECTION: ERROR PATTERNS & CORRECTIONS TABLE ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column (4 Cols): Error Patterns */}
        <div className="lg:col-span-4 bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-ledger-border">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-brand" />
                <h2 className="text-sm font-bold text-text-pri">Distribusi Tag Kesalahan</h2>
              </div>
              <span className="text-[11px] text-text-sec">
                {(byTag || []).length} Tipe
              </span>
            </div>

            <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1">
              {(!byTag || byTag.length === 0) ? (
                <div className="py-8 text-center text-xs text-text-sec">Belum ada data error tag</div>
              ) : (
                byTag.map((item, idx) => {
                  const maxCount = byTag[0]?.count || 1;
                  const percent = Math.round((item.count / maxCount) * 100);
                  const isSelected = tagFilter === item.tag;

                  return (
                    <div
                      key={idx}
                      onClick={() => setTagFilter(isSelected ? 'ALL' : item.tag)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer ${
                        isSelected
                          ? 'border-brand bg-brand-low/50 shadow-2xs'
                          : 'border-ledger-border/70 hover:bg-surface-hover'
                      }`}
                    >
                      <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-semibold text-text-pri capitalize">
                          {item.tag ? item.tag.replace(/_/g, ' ') : 'Lainnya'}
                        </span>
                        <span className="font-mono font-bold text-brand text-[11px]">
                          {item.count} temuan
                        </span>
                      </div>
                      <div className="w-full bg-paper rounded-full h-1.5 overflow-hidden border border-ledger-border/40">
                        <div 
                          className="bg-brand h-full rounded-full transition-all duration-300" 
                          style={{ width: `${Math.max(percent, 4)}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Column (8 Cols): Corrections Table */}
        <div className="lg:col-span-8 bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between">
          <div>
            {/* Table Header & Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 mb-4 border-b border-ledger-border">
              <div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-brand" />
                  <h2 className="text-sm font-bold text-text-pri">Log Koreksi & Evaluasi Ahli</h2>
                </div>
                <p className="text-text-sec text-xs mt-0.5">
                  Daftar umpan balik pakar pajak untuk penyempurnaan akurasi TACIA.
                </p>
              </div>

              {/* Rows Per Page */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-text-sec">Baris:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="bg-input-bg text-text-pri border border-ledger-border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand font-medium cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Filter Pills & Search Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
              {/* Rating Filter Pills */}
              <div className="flex items-center gap-1 bg-paper border border-ledger-border rounded-lg p-0.5">
                {[
                  { key: 'ALL', label: 'Semua' },
                  { key: 'up', label: 'Upvote' },
                  { key: 'down', label: 'Koreksi' }
                ].map(r => (
                  <button
                    key={r.key}
                    onClick={() => { setRatingFilter(r.key); setCurrentPage(1); }}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                      ratingFilter === r.key
                        ? 'bg-card-bg text-brand shadow-xs border border-ledger-border'
                        : 'text-text-sec hover:text-text-pri'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              {/* Search Box */}
              <div className="relative flex-1 sm:max-w-xs">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-sec" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  placeholder="Cari pertanyaan / komentar..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-input-bg border border-ledger-border text-text-pri focus:outline-none focus:ring-1 focus:ring-brand placeholder:text-muted transition"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto pr-1">
              <table className="w-full text-xs text-left">
                <thead className="sticky top-0 bg-card-bg border-b border-ledger-border text-text-sec font-semibold z-10">
                  <tr>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Error Tag</th>
                    <th className="py-2.5 px-3 w-1/2">Pertanyaan & Catatan Pakar</th>
                    <th className="py-2.5 px-3 text-right">Waktu</th>
                    <th className="py-2.5 px-3 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ledger-border/40">
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-text-sec">
                        {searchQuery || ratingFilter !== 'ALL' || tagFilter !== 'ALL'
                          ? 'Tidak ada feedback yang sesuai dengan kriteria filter'
                          : 'Belum ada catatan koreksi RLHF.'}
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((row, i) => (
                      <tr key={i} className="hover:bg-surface-hover transition-colors">
                        <td className="py-3 px-3 whitespace-nowrap">
                          {row.rating === 'up' ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold border border-emerald-500/20 text-[10px]">
                              <ThumbsUp className="w-3 h-3" /> Upvote
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400 font-semibold border border-rose-500/20 text-[10px]">
                              <ThumbsDown className="w-3 h-3" /> Downvote
                            </span>
                          )}
                        </td>

                        <td className="py-3 px-3">
                          {row.error_tag ? (
                            <div className="flex flex-wrap gap-1">
                              {row.error_tag.split(',').map((tag, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-paper border border-ledger-border text-text-sec rounded font-mono text-[9px] uppercase">
                                  {tag.trim().replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-text-sec">—</span>
                          )}
                        </td>

                        <td className="py-3 px-3 max-w-md">
                          <div className="space-y-1.5">
                            <p className="text-text-pri font-medium text-[11px] line-clamp-2" title={row.question}>
                              {row.question || "—"}
                            </p>
                            {row.comment && (
                              <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-700 dark:text-amber-300">
                                <span className="font-bold block text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400">
                                  Catatan Pakar:
                                </span>
                                {row.comment}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-3 text-right text-[11px] font-mono text-text-sec whitespace-nowrap">
                          {row.created_at ? new Date(row.created_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }) : '—'}
                        </td>

                        <td className="py-3 px-3 text-center whitespace-nowrap">
                          {row.id ? (
                            <button 
                              onClick={() => handleDeleteFeedback(row.id)}
                              className="text-text-sec hover:text-rose-500 p-1.5 rounded-lg hover:bg-rose-500/10 transition"
                              title="Hapus Catatan"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <span className="text-text-sec">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="pt-4 mt-4 border-t border-ledger-border flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="text-xs text-text-sec font-medium">
                Menampilkan {totalFiltered > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, totalFiltered)} dari {totalFiltered} log feedback
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-ledger-border bg-card-bg text-text-sec hover:text-text-pri hover:bg-surface-hover disabled:opacity-40 disabled:pointer-events-none transition"
                  title="Halaman Sebelumnya"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="px-3 py-1 rounded-lg text-xs font-mono font-semibold bg-paper border border-ledger-border text-text-pri">
                  {page} / {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-lg border border-ledger-border bg-card-bg text-text-sec hover:text-text-pri hover:bg-surface-hover disabled:opacity-40 disabled:pointer-events-none transition"
                  title="Halaman Berikutnya"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
